import { comics } from '@/mocks/comics';
import { readerPanels } from '@/mocks/readerPanels';
import { categories } from '@/mocks/categories';
import type { Chapter, Comic, Category } from '@/types/comic';

const chapterSubtitles = [
  '初次相遇',
  '意外的告白',
  '雨夜的约定',
  '沉默的心意',
  '新的敌人',
  '并肩作战',
  '真相的碎片',
  '无法说出口',
  '命运的重逢',
  '决裂之时',
  '暗流涌动',
  '守护的誓言',
  '错位的错过',
  '逆转的瞬间',
  '最后的赌注',
  '心动信号',
  '旧梦重温',
  '风暴前夜',
  '重逢之日',
  '未完的故事',
];

function shiftDate(base: string, daysBack: number): string {
  const d = new Date(base + 'T00:00:00');
  d.setDate(d.getDate() - daysBack);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getComicById(id: string): Comic | undefined {
  return comics.find((c) => c.id === id);
}

export function getCategoryByKey(key: string): Category | undefined {
  return categories.find((c) => c.key === key || c.name === key);
}

export function buildChapters(comic: Comic): Chapter[] {
  const list: Chapter[] = [];
  for (let n = comic.chapterCount; n >= 1; n -= 1) {
    list.push({
      id: `ch-${n}`,
      index: n,
      title: `第${n}话 · ${chapterSubtitles[n % chapterSubtitles.length]}`,
      updatedAt: shiftDate(comic.updatedAt, comic.chapterCount - n),
    });
  }
  return list;
}

export function getChapter(comic: Comic, chapterId: string): Chapter | undefined {
  return buildChapters(comic).find((c) => c.id === chapterId);
}

export function getChapterPages(comic: Comic, chapterId: string): string[] {
  const num = Number(chapterId.replace('ch-', '')) || 1;
  const count = 6 + (num % 4);
  const start = (Number(comic.id.replace('c', '')) + num) % readerPanels.length;
  const pages: string[] = [];
  for (let i = 0; i < count; i += 1) {
    pages.push(readerPanels[(start + i) % readerPanels.length]);
  }
  return pages;
}

export function formatCount(value: number): string {
  if (value >= 10000) {
    const v = value / 10000;
    return `${v >= 100 ? Math.round(v) : v.toFixed(1)}万`;
  }
  return String(value);
}

export function categoryName(key: string): string {
  const c = getCategoryByKey(key);
  return c ? c.name : key;
}

export interface ComicQuery {
  category?: string;
  keyword?: string;
  region?: string;
  status?: string;
  tag?: string;
  sort?: 'updated' | 'hot' | 'favorite';
}

export function queryComics(params: ComicQuery): Comic[] {
  const { category, keyword, region, status, tag, sort = 'updated' } = params;
  let result = [...comics];

  if (category) {
    const cat = getCategoryByKey(category);
    const key = cat ? cat.key : category;
    result = result.filter((c) => c.categories.includes(key));
  }

  if (region && region !== '全部') {
    result = result.filter((c) => c.region === region);
  }

  if (status && status !== '全部') {
    result = result.filter((c) => c.status === status);
  }

  if (tag && tag !== '全部') {
    result = result.filter((c) => c.categories.includes(tag));
  }

  if (keyword && keyword.trim()) {
    const kw = keyword.trim().toLowerCase();
    result = result.filter(
      (c) =>
        c.title.toLowerCase().includes(kw) ||
        c.author.toLowerCase().includes(kw) ||
        c.description.toLowerCase().includes(kw),
    );
  }

  if (sort === 'hot') {
    result.sort((a, b) => b.views - a.views);
  } else if (sort === 'favorite') {
    result.sort((a, b) => b.favorites - a.favorites);
  } else {
    result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }

  return result;
}

export function getRelatedComics(comic: Comic, limit = 6): Comic[] {
  return comics
    .filter((c) => c.id !== comic.id)
    .map((c) => {
      const shared = c.categories.filter((k) => comic.categories.includes(k)).length;
      const sameAuthor = c.author === comic.author ? 2 : 0;
      return { comic: c, score: shared * 3 + sameAuthor };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || b.comic.views - a.comic.views)
    .slice(0, limit)
    .map((x) => x.comic);
}

export function getBannerComics(): Comic[] {
  return comics.filter((c) => !!c.banner);
}

export function getLatestComics(limit = 8): Comic[] {
  return [...comics].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, limit);
}

export function getPopularComics(limit = 8): Comic[] {
  return [...comics].sort((a, b) => b.views - a.views).slice(0, limit);
}